import {createChart, ColorType, CrosshairMode} from 'lightweight-charts';
import React, { useEffect, useRef } from 'react';
import {formatTimestampInMs, intervalToMilliseconds} from "../commons/utils";

export const ChartComponent = props => {
  const {
    symbol,
    data,
    markers,
    profits,
    indicators,
    selectedIndicators,
    colorMap,
    timeframe,
    colors: {
      backgroundColor = 'white',
      lineColor = '#2962FF',
      textColor = 'black',
      areaTopColor = '#2962FF',
      areaBottomColor = 'rgba(41, 98, 255, 0.28)',
    } = {},
  } = props;
  const chartContainerRef = useRef();
  const myPriceFormatter = p => {
    const v = Math.abs(p);
    if (v < 10) {
      return p.toFixed(6);
    } else if (v < 100) {
      return p.toFixed(5);
    } else if (v < 1000) {
      return p.toFixed(4);
    } else if (v < 1000) {
      return p.toFixed(3);
    } else {
      return p.toFixed(2);
    }
  }


  useEffect(
    () => {
      const handleResize = () => {
        chart.applyOptions({ width: chartContainerRef.current.clientWidth });
      };


      const chart = createChart(chartContainerRef.current, {
        width: chartContainerRef.current.clientWidth,
        height: 580,
        rightPriceScale: {
          visible: true,
          borderColor: 'rgba(197, 203, 206, 1)',
        },
        leftPriceScale: {
          visible: true,
          borderColor: 'rgba(197, 203, 206, 1)',
        },
        layout: {
          background: {
            type: 'solid',
            color: '#ffffff',
          },
          textColor: 'rgba(33, 56, 77, 1)',
        },
        grid: {
          horzLines: {
            color: '#F0F3FA',
          },
          vertLines: {
            color: '#F0F3FA',
          },
        },
        crosshair: {
          mode: CrosshairMode.Normal,
        },
        timeScale: {
          borderColor: 'rgba(197, 203, 206, 1)',
          minBarSpacing: 0.001
        },
        handleScroll: {
          vertTouchDrag: false,
        },
        localization: {
          timeFormatter: ts => {
              return formatTimestampInMs(ts * 1000);
          },
          priceFormatter: myPriceFormatter,
        }
      });
      chart.timeScale().fitContent();

      const newSeries = chart.addCandlestickSeries({ upColor: '#26a69a', downColor: '#ef5350', borderVisible: false, wickUpColor: '#26a69a', wickDownColor: '#ef5350' });
      if (data && data.length > 0) {
        const newData = [];

        data.forEach(o => {
          if (o.time) {
            const last = newData[newData.length - 1];
            if (!last || o.time > last.time) {
              newData.push({ ...o }); // Shallow copy
            }
          }
        });
        newSeries.setData(newData);
      }
      if (markers && markers.length > 0) {
        newSeries.setMarkers(markers);
      }


      if (profits && profits.length > 0) {
        const lineSeriesProfit = chart.addLineSeries({ color: '#2962FF', lineWidth: 1, priceScaleId: 'left' });
        lineSeriesProfit.priceScale().applyOptions({
          // set the positioning of the volume series
          scaleMargins: {
            top: 0.3, // highest point of the series will be 70% away from the top
            bottom: 0.4,
          },
        });
        lineSeriesProfit.setData(profits);
      }

      const indicatorItemList = []
      // #26a69a #1dad2e #1d27ad #801dad #ad1d8c
      // const colorList = ['#26a69a','#801dad', '#1d27ad', '#ad1d8c'];
      const groupMarginList = [0.65, 0.45, 0.25, 0.05];
      const groupMarginMap = {};

      // let linePriceColorCounter = 0;
      // let nonLinePriceColorCounter = 0;

      if (indicators && indicators.length > 0) {
        indicators.forEach(indicator => {
          const timeframeMs = intervalToMilliseconds(timeframe.replace("_", ""));
          if (symbol.toLowerCase() === indicator.symbol.toLowerCase() &&
              intervalToMilliseconds(timeframe.replace("_", "")) <= intervalToMilliseconds(indicator.interval.replace("_", "")) &&
              selectedIndicators.includes(indicator.name)) {
            console.log(indicator.symbol + " " + symbol);
            let priceScaleId = 'right';
            if (!indicator.usePriceScale) {
              priceScaleId = 'default';
              if (indicator.scaleGroup && groupMarginList.length > 0 ) {
                priceScaleId = indicator.scaleGroup;
              }
              if (!groupMarginMap.hasOwnProperty(priceScaleId)) {
                groupMarginMap[priceScaleId] = groupMarginList.pop();
              }
            }
            const lineIndicator = chart.addLineSeries({
              color: colorMap.get(indicator.name),
              lineWidth: 1,
              overlay: true,
              priceScaleId: priceScaleId
            });
            if (priceScaleId !== 'right') {
              lineIndicator.priceScale().applyOptions({
                // set the positioning of the volume series
                scaleMargins: {
                  top: 1 - 0.15 - groupMarginMap[priceScaleId], // highest point of the series
                  bottom: groupMarginMap[priceScaleId],
                },
              });
            }
            const indicatorList = [];
            indicator.data.forEach(o => {
              if (o.t) {
                const time = (o.t + 1 - timeframeMs) / 1000;
                const last = indicatorList[indicatorList.length - 1];
                if (!last || time > last.time) {
                  indicatorList.push({
                    time,
                    value: o.v ? o.v : 0
                  });
                }
              }
            });
            if (indicator.axis) {
              indicator.axis.forEach(axisValue => {
                const lineAxis = chart.addLineSeries({
                  color: 'gray',
                  lineWidth: 1,
                  overlay: true,
                  priceScaleId: priceScaleId
                });
                const axisData = [];
                indicator.data.forEach(o => {
                  if (o.t) {
                    axisData.push({
                      time: (o.t + 1 - timeframeMs) / 1000,
                      value: axisValue
                    });
                  }
                });
                lineAxis.setData(axisData);
              });
            }
            lineIndicator.setData(indicatorList);
            indicatorItemList.push({
              line: lineIndicator,
              name: indicator.name,
              symbol: indicator.symbol,
              usePriceScale: indicator.usePriceScale
            });
          }
        });
      }
      let toolTip = document.getElementById("tooltip");

      if (!toolTip) {
        // Create a new div element
        toolTip = document.createElement('div');
        toolTip.id = "tooltip";
        toolTip.style = `width: 170px; position: absolute; display: none; padding: 3px; box-sizing: border-box; font-size: 12px; text-align: left; z-index: 1000; top: 12px; left: 12px; pointer-events: none; border: 1px solid; border-radius: 2px;font-family: -apple-system, BlinkMacSystemFont, 'Trebuchet MS', Roboto, Ubuntu, sans-serif; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale;`;
        toolTip.style.height = `20px`;
        toolTip.style.background = 'white';
        toolTip.style.color = 'black';
        toolTip.style.borderColor = '#2962FF';
        toolTip.style.display = 'none';
        chartContainerRef.current.appendChild(toolTip);
      }

      chart.subscribeCrosshairMove(param => {
        if (
          param.point === undefined ||
          !param.time ||
          param.point.x < 0 ||
          param.point.y < 0
        ) {
          toolTip.style.display = 'none';
        } else {
          const items = [];
          const data0 = param.seriesData.get(newSeries);
          if (data0 && data0.time) {
            items.push(`o: ${myPriceFormatter(data0.open)} c: ${myPriceFormatter(data0.close)}`);
            items.push(`h: ${myPriceFormatter(data0.high)} l: ${myPriceFormatter(data0.low)}`);
          }
          indicatorItemList.forEach(indicatorItem => {
            const idcData = param.seriesData.get(indicatorItem.line);
            if (idcData) {
              items.push(`<span style='color: ${colorMap.get(indicatorItem.name)} '>${indicatorItem.name}</span>: ${myPriceFormatter(idcData.value)}`);
            }
          });
          if (items.length > 0) {
            const height = 20 * items.length;
            toolTip.style.height = `${height}px`;
            toolTip.innerHTML = `<div>`;
            items.forEach(value => {
              toolTip.innerHTML += `${value}<br />`;
            })
            toolTip.innerHTML += `</div>`;

            // Position tooltip according to mouse cursor position
            toolTip.style.left = param.point.x + 'px';
            toolTip.style.top = (param.point.y - 50) + 'px';
            toolTip.style.display = 'block';
          } else {
            toolTip.style.display = 'none';
          }

        }
      });

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);

        chart.remove();
      };
    },
    [data, markers, backgroundColor, lineColor, textColor, areaTopColor, areaBottomColor, selectedIndicators]
  );

  return (
    <div
      ref={chartContainerRef}
    />
  );
};
